import { FileText, Loader2, Sparkles, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { KnowledgeSourceModal } from '../conversation';
import {
  Badge,
  Button,
  BuyTokensModal,
  Input,
  InsufficientTokensModal,
} from '../ui';
import { useAuth } from '../../contexts';
import { useKnowledgeSources, useTokens } from '../../hooks';
import { ExecutingAgentService } from '../../services';
import { Agent, KnowledgeSource } from '../../types/entities';
import { parseStreamResult, readStreamFinalMessage } from '../../utils/aiStream';

export interface TaskFormValues {
  description: string;
  expected_output: string;
  tools: string[];
  tool_mode: 'creative' | 'accuracy';
  context: string[];
  is_active: boolean;
  result_format: '' | 'json' | 'text' | 'markdown';
}

export type AgentContext = Pick<
  Agent,
  'name' | 'role' | 'goal' | 'backstory' | 'tools'
>;

interface TaskFormPanelProps {
  mode: 'create' | 'edit';
  creationMode?: 'ai' | 'manual';
  organizationId?: number;
  defaultValues: TaskFormValues;
  submitLabel: string;
  onSubmit: (values: TaskFormValues) => Promise<void> | void;
  onCancel?: () => void;
  layout?: 'split' | 'stack';
  agentContext?: AgentContext | null;
  showSubmitButton?: boolean;
  onGenerateSuccess?: (values: TaskFormValues) => void;
  lockToolMode?: 'creative' | 'accuracy';
  quickGeneratePrompt?: string;
  quickGenerateLabel?: string;
}

interface GeneratedTaskData {
  description?: string;
  expected_output?: string;
  tools?: string[];
  tool_mode?: 'creative' | 'accuracy';
  context?: string[];
  is_active?: boolean;
}

const ENABLED_CREW_TOOLS = [
  'ragtool',
  'codeinterpretertool',
  'filereadtool',
  'directoryreadtool',
  'directorysearchtool',
  'csvsearchtool',
  'jsonsearchtool',
  'txtsearchtool',
  'xmlsearchtool',
  'mdxsearchtool',
  'pdfsearchtool',
  'docxsearchtool',
  'websitesearchtool',
  'scrapewebsitetool',
  'scrapeelementfromwebsitetool',
  'youtubechannelsearchtool',
  'youtubevideosearchtool',
  'githubsearchtool',
] as const;

const TaskFormPanel: React.FC<TaskFormPanelProps> = ({
  mode,
  creationMode = 'ai',
  organizationId,
  defaultValues,
  submitLabel,
  onSubmit,
  onCancel,
  layout = 'split',
  agentContext,
  showSubmitButton = true,
  onGenerateSuccess,
  lockToolMode,
  quickGeneratePrompt,
  quickGenerateLabel,
}) => {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const {
    checkTokensBeforeAction,
    tokensBalance,
    isModalOpen,
    closeModal,
    isBuyTokensModalOpen,
    closeBuyTokensModal,
  } = useTokens();
  const { knowledgeSources, loadKnowledgeSources, toggleKnowledgeSource, isLoadingKnowledge } =
    useKnowledgeSources(organizationId);
  const executingAgentService = useMemo(() => new ExecutingAgentService(), []);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
    getValues,
  } = useForm<TaskFormValues>({ defaultValues });

  const [isGenerating, setIsGenerating] = useState(false);
  const [description, setDescription] = useState('');
  const [selectedKnowledgeSources, setSelectedKnowledgeSources] = useState<
    KnowledgeSource[]
  >([]);
  const [showKnowledgeSourceModal, setShowKnowledgeSourceModal] =
    useState(false);

  const hasExistingData = mode === 'edit' && !!defaultValues.description;
  const [hasReceivedGeneration, setHasReceivedGeneration] = useState(false);
  const [editingTaskField, setEditingTaskField] = useState<
    keyof TaskFormValues | null
  >(null);
  const [editingTaskValue, setEditingTaskValue] = useState('');

  const isManualMode = creationMode === 'manual';
  const useQuickGenerate = Boolean(quickGeneratePrompt) && mode === 'create' && !isManualMode;
  const shouldShowGenerator = !isManualMode && !useQuickGenerate;
  const showForm = isManualMode || hasExistingData || hasReceivedGeneration;
  const showManualCreateFields = isManualMode && mode === 'create';

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!organizationId) {
      return;
    }
    loadKnowledgeSources();
  }, [loadKnowledgeSources, organizationId]);

  /**
   * Aplica os dados gerados pela IA no formulário.
   * @param taskData Dados retornados pela IA
   */
  const applyGeneratedTaskData = (taskData: GeneratedTaskData): TaskFormValues => {
    const currentValues = getValues();
    const inferredMode = Array.isArray(taskData?.tools)
      ? mapToolsToMode(taskData.tools)
      : currentValues.tool_mode;
    const resolvedToolMode = lockToolMode || taskData?.tool_mode || inferredMode;
    const updatedValues: TaskFormValues = {
      description: taskData?.description || currentValues.description,
      expected_output: taskData?.expected_output || currentValues.expected_output,
      tools: lockToolMode
        ? mapModeToTools(lockToolMode)
        : Array.isArray(taskData?.tools)
        ? taskData.tools
        : currentValues.tools,
      tool_mode: resolvedToolMode,
      context: taskData?.context || currentValues.context,
      is_active:
        taskData?.is_active !== undefined
          ? taskData.is_active
          : currentValues.is_active,
      result_format: currentValues.result_format,
    };

    Object.entries(updatedValues).forEach(([key, value]) => {
      setValue(key as keyof TaskFormValues, value, { shouldDirty: true });
    });

    return updatedValues;
  };

  /**
   * Extrai os dados da task do payload retornado pela IA.
   * @param resultData Resultado parseado do stream
   * @returns Dados da task
   */
  const getTaskFromResult = (resultData: any): GeneratedTaskData => {
    if (resultData?.tasks && Array.isArray(resultData.tasks)) {
      return resultData.tasks[0] || {};
    }
    if (resultData?.task) {
      return resultData.task;
    }
    if (resultData?.description) {
      return resultData;
    }
    return resultData || {};
  };

  const buildAgentContextJson = () => {
    if (!agentContext) {
      return JSON.stringify({});
    }
    return JSON.stringify({
      name: agentContext.name,
      role: agentContext.role || '',
      goal: agentContext.goal || '',
      backstory: agentContext.backstory || '',
      tools: agentContext.tools || [],
    });
  };

  const mapToolsToMode = (tools: string[] | undefined): 'creative' | 'accuracy' => {
    if (!Array.isArray(tools) || tools.length === 0) {
      return 'accuracy';
    }
    const normalizedTools = tools.map(tool => (tool || '').toLowerCase().trim());
    const hasRagTool = normalizedTools.includes('ragtool');
    const hasCodeInterpreterTool = normalizedTools.includes('codeinterpretertool');

    if (hasRagTool && !hasCodeInterpreterTool) {
      return 'creative';
    }
    if (hasCodeInterpreterTool && !hasRagTool) {
      return 'accuracy';
    }
    return 'accuracy';
  };

  const mapModeToTools = (mode: 'creative' | 'accuracy'): string[] =>
    mode === 'creative' ? [...ENABLED_CREW_TOOLS] : [...ENABLED_CREW_TOOLS];

  useEffect(() => {
    if (!lockToolMode) return;
    setValue('tool_mode', lockToolMode, { shouldDirty: true });
    setValue('tools', mapModeToTools(lockToolMode), { shouldDirty: true });
  }, [lockToolMode, setValue]);

  const handleGenerate = async (overridePrompt?: string) => {
    const prompt = (overridePrompt ?? description).trim();
    if (!prompt) {
      toast.error(
        mode === 'create'
          ? 'Por favor, descreva a task que deseja criar'
          : 'Por favor, descreva as alterações desejadas para a task'
      );
      return;
    }

    if (mode === 'create' && !agentContext) {
      toast.error('Por favor, selecione um agente para vincular a task');
      return;
    }

    if (!checkTokensBeforeAction()) {
      return;
    }

    setIsGenerating(true);

    try {
      const knowledgeSourceIds = selectedKnowledgeSources.map(ks => ks.id);
      const hasRag = knowledgeSourceIds.length > 0;
      const resultFormat = getValues('result_format') || undefined;
      const agentContextJson = buildAgentContextJson();

      if (mode === 'edit') {
        const stream = await executingAgentService.generateTaskStream({
          description: prompt,
          agent_context: agentContextJson,
          format_expected: resultFormat,
          rag_knowledge_source_ids: hasRag ? knowledgeSourceIds : undefined,
          user_message: prompt,
        });

        const rawResponse = await readStreamFinalMessage(stream);
        const resultData = parseStreamResult(rawResponse);
        const taskData = getTaskFromResult(resultData);
        const updatedValues = applyGeneratedTaskData(taskData);
        setHasReceivedGeneration(true);
        onGenerateSuccess?.(updatedValues);

        toast.success(
          'Task atualizada com sucesso! Revise os campos e ajuste se necessário.'
        );
      } else {
        const stream = await executingAgentService.generateTaskStream({
          description: prompt,
          agent_context: agentContextJson,
          format_expected: resultFormat,
          rag_knowledge_source_ids: hasRag ? knowledgeSourceIds : undefined,
          user_message: prompt,
        });

        const rawResponse = await readStreamFinalMessage(stream);
        const resultData = parseStreamResult(rawResponse);
        const taskData = getTaskFromResult(resultData);
        const updatedValues = applyGeneratedTaskData(taskData);
        setHasReceivedGeneration(true);
        onGenerateSuccess?.(updatedValues);

        toast.success(
          'Task gerada com sucesso! Revise os campos e ajuste se necessário.'
        );
      }

      await refreshUser();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao gerar task: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatTaskFieldValue = (
    field: keyof TaskFormValues,
    value: TaskFormValues[keyof TaskFormValues]
  ): string => {
    if (field === 'tool_mode') {
      return value === 'creative'
        ? t('tasks.modeCreative')
        : t('tasks.modeAccuracy');
    }
    if (field === 'context' && Array.isArray(value)) {
      return value.join(', ');
    }
    return value ? String(value) : '';
  };

  const parseTaskFieldValue = (
    field: keyof TaskFormValues,
    value: string
  ): TaskFormValues[keyof TaskFormValues] => {
    if (field === 'context') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
    return value;
  };

  const startEditingTask = (field: keyof TaskFormValues) => {
    setEditingTaskField(field);
    setEditingTaskValue(
      formatTaskFieldValue(field, getValues(field))
    );
  };

  const saveEditingTask = () => {
    if (!editingTaskField) return;
    const updatedValue = parseTaskFieldValue(
      editingTaskField,
      editingTaskValue
    );
    setValue(editingTaskField, updatedValue, { shouldDirty: true });
    setEditingTaskField(null);
  };

  const isSplitLayout = layout === 'split';

  const taskDetailFields: Array<{
    key: 'description' | 'expected_output' | 'tool_mode';
    label: string;
    type: 'text' | 'textarea' | 'select';
  }> = [
    { key: 'description', label: t('tasks.taskDescription'), type: 'textarea' },
    {
      key: 'expected_output',
      label: t('tasks.expectedOutput'),
      type: 'textarea',
    },
    ...(!lockToolMode
      ? ([{ key: 'tool_mode', label: t('tasks.toolMode'), type: 'select' }] as const)
      : []),
  ];

  return (
    <div className={`flex h-full ${isSplitLayout ? 'flex-row' : 'flex-col'}`}>
      {shouldShowGenerator && (
        <div
          className={`${
            isSplitLayout
              ? `flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden ${
                  showForm ? 'flex-[1.5]' : 'flex-1'
                }`
              : 'p-6 border-b border-gray-200 dark:border-gray-700'
          }`}
        >
          <div
            className={`${
              isSplitLayout ? 'p-6 border-b border-gray-200 dark:border-gray-700' : 'mb-4'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Sparkles className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {mode === 'create'
                  ? 'Descreva a task que deseja criar'
                  : 'Descreva as alterações desejadas'}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {mode === 'create'
                ? 'Descreva em detalhes como você quer que a task seja. O assistente irá gerar automaticamente os campos do formulário.'
                : 'Descreva as alterações que deseja fazer na task. O assistente irá atualizar os campos do formulário considerando as informações atuais.'}
            </p>
          </div>

          <div className={isSplitLayout ? 'flex-1 p-6 flex flex-col' : ''}>
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fontes de Conhecimento (opcional)
                </label>
                <Button
                  variant="outline"
                  onClick={() => setShowKnowledgeSourceModal(true)}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Selecionar
                </Button>
              </div>
              {selectedKnowledgeSources.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedKnowledgeSources.map(source => (
                    <Badge
                      key={source.id}
                      variant="primary"
                      className="flex items-center space-x-1"
                    >
                      <span>{source.name}</span>
                      <button
                        onClick={() =>
                          setSelectedKnowledgeSources(prev =>
                            prev.filter(s => s.id !== source.id)
                          )
                        }
                        className="ml-1 hover:text-red-500 dark:hover:text-red-400"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Nenhuma fonte selecionada
                </p>
              )}
            </div>

            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full px-4 py-3 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              style={{ height: isSplitLayout ? '250px' : '200px' }}
              placeholder="Exemplo: Quero uma task que analise dados de vendas, identifique tendências e gere um relatório..."
            />

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tasks.resultFormat')}
              </label>
              <select
                className="select-control"
                {...register('result_format')}
              >
                <option value="">{t('tasks.selectResultFormat')}</option>
                <option value="json">{t('tasks.resultFormatJson')}</option>
                <option value="text">{t('tasks.resultFormatText')}</option>
                <option value="markdown">{t('tasks.resultFormatMarkdown')}</option>
              </select>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  void handleGenerate();
                }}
                disabled={isGenerating || !description.trim()}
                className="min-w-[150px]"
                type="button"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {mode === 'create' ? 'Gerar Task' : 'Atualizar Task'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {useQuickGenerate && !hasReceivedGeneration && (
        <div className="flex-1 flex items-center justify-center p-6">
          <Button
            variant="outline"
            onClick={() => handleGenerate(quickGeneratePrompt)}
            disabled={isGenerating}
            className="min-w-[320px] max-w-full"
            type="button"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                {quickGenerateLabel || 'Gerar tarefa para esse agente'}
              </>
            )}
          </Button>
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleSubmit(
            data => {
              const normalizedData: TaskFormValues = lockToolMode
                ? {
                    ...data,
                    tool_mode: lockToolMode,
                    tools: mapModeToTools(lockToolMode),
                  }
                : data;
              if (!data.description?.trim()) {
                toast.error('Descrição é obrigatória');
                return;
              }
              if (!data.expected_output?.trim()) {
                toast.error('Saída esperada é obrigatória');
                return;
              }
              onSubmit(normalizedData);
            }
          )}
          className={`${
            isSplitLayout
              ? shouldShowGenerator
                ? 'flex-[2] overflow-y-auto p-6 border-l border-gray-200 dark:border-gray-700'
                : 'flex-1 overflow-y-auto p-6'
              : 'p-6'
          }`}
        >
          <div className="space-y-4 max-w-2xl mx-auto">
            <input type="hidden" {...register('tool_mode')} />
            {showManualCreateFields ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('tasks.taskDescription')}
                  </label>
                  <textarea
                    rows={6}
                    className="w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    {...register('description')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('tasks.expectedOutput')}
                  </label>
                  <textarea
                    rows={6}
                    className="w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    {...register('expected_output')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('tasks.resultFormat')}
                  </label>
                  <select className="select-control" {...register('result_format')}>
                    <option value="">{t('tasks.selectResultFormat')}</option>
                    <option value="json">{t('tasks.resultFormatJson')}</option>
                    <option value="text">{t('tasks.resultFormatText')}</option>
                    <option value="markdown">{t('tasks.resultFormatMarkdown')}</option>
                  </select>
                </div>
              </>
            ) : (
              taskDetailFields.map(field => {
              const isEditing = editingTaskField === field.key;
              const displayValue = formatTaskFieldValue(
                field.key,
                getValues(field.key)
              );
              return (
                <div
                  key={field.key}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {field.label}
                      </p>
                      {field.key === 'tool_mode' ? (
                        isEditing ? (
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className={
                                getValues('tool_mode') === 'creative'
                                  ? 'border-primary-400 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-300'
                                  : ''
                              }
                              onClick={() =>
                                setValue('tool_mode', 'creative', {
                                  shouldDirty: true,
                                })
                              }
                            >
                              {t('tasks.modeCreative')}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className={
                                getValues('tool_mode') === 'accuracy'
                                  ? 'border-primary-400 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-300'
                                  : ''
                              }
                              onClick={() =>
                                setValue('tool_mode', 'accuracy', {
                                  shouldDirty: true,
                                })
                              }
                            >
                              {t('tasks.modeAccuracy')}
                            </Button>
                          </div>
                        ) : (
                          <Badge variant="primary" className="mt-2 inline-flex items-center">
                            {displayValue}
                          </Badge>
                        )
                      ) : isEditing ? (
                        field.type === 'textarea' ? (
                          <textarea
                            value={editingTaskValue}
                            onChange={event =>
                              setEditingTaskValue(event.target.value)
                            }
                            rows={20}
                            className="mt-2 w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          />
                        ) : (
                          <Input
                            value={editingTaskValue}
                            onChange={event =>
                              setEditingTaskValue(event.target.value)
                            }
                            className="mt-2"
                          />
                        )
                      ) : (
                        <p className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                          {displayValue || '-'}
                        </p>
                      )}
                    </div>
                    <div className="mt-4">
                      {isEditing ? (
                        <Button size="sm" type="button" onClick={saveEditingTask}>
                          {t('common.save')}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={() => startEditingTask(field.key)}
                        >
                          {t('common.edit')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }))}

            {!showManualCreateFields && isManualMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tasks.resultFormat')}
                </label>
                <select
                  className="select-control"
                  {...register('result_format')}
                >
                  <option value="">{t('tasks.selectResultFormat')}</option>
                  <option value="json">{t('tasks.resultFormatJson')}</option>
                  <option value="text">{t('tasks.resultFormatText')}</option>
                  <option value="markdown">{t('tasks.resultFormatMarkdown')}</option>
                </select>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register('is_active')}
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {t('tasks.taskActive')}
              </span>
            </div>

            {(onCancel || showSubmitButton) && (
              <div className="flex justify-end space-x-2">
                {onCancel && (
                  <Button type="button" variant="outline" onClick={onCancel}>
                    {t('common.cancel')}
                  </Button>
                )}
                {showSubmitButton && (
                  <Button type="submit" variant="primary" disabled={isSubmitting}>
                    {submitLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
        </form>
      )}

      <KnowledgeSourceModal
        isOpen={showKnowledgeSourceModal}
        onClose={() => setShowKnowledgeSourceModal(false)}
        knowledgeSources={knowledgeSources}
        selectedKnowledgeSources={selectedKnowledgeSources}
        isLoading={isLoadingKnowledge}
        onToggleSource={source => {
          toggleKnowledgeSource(
            source,
            selectedKnowledgeSources,
            setSelectedKnowledgeSources
          );
        }}
      />

      <InsufficientTokensModal
        isOpen={isModalOpen}
        onClose={closeModal}
        tokensBalance={tokensBalance}
      />
      <BuyTokensModal
        isOpen={isBuyTokensModalOpen}
        onClose={closeBuyTokensModal}
      />
    </div>
  );
};

export default TaskFormPanel;
